import axios from "axios";
import { config } from "@/config";
import { clearAuthToken, loadAuthToken } from "@/utils/auth-token";

export interface PlanTier {
  range: string;
  rate: string;
}

export interface UserPlan {
  firstName: string;
  vehicleModel: string;
  periodicity: string;
  planFamily: string;
}

export interface CurrentPlan {
  fixedValue: string;
  tiers: PlanTier[];
}

export interface NewPlan {
  fixedValue: string;
  linearRate: string;
}

export interface PlanComparison {
  user: UserPlan;
  currentPlan: CurrentPlan;
  newPlan: NewPlan;
}

export type StatusResult = "form" | "success" | "expired";

export type EligibilityResult =
  | { kind: "eligible"; plan: PlanComparison }
  | { kind: "ineligible_comfort" }
  | { kind: "already_migrated" }
  // Any other ineligibility the backend reports (reason "invalid" or a value
  // this client doesn't recognize). The user is authenticated — we should
  // tell them migration isn't available, not log them out.
  | { kind: "ineligible_other"; reason: string | null };

const migrationBase = (): string => `${config.migrationApiUrl}/v1/migracao`;

const authHeaders = (): Record<string, string> => {
  const token = loadAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const handle401 = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    clearAuthToken();
  }
  throw error;
};

export const fetchStatus = async (): Promise<StatusResult> => {
  try {
    const response = await axios.get<{ state?: StatusResult }>(
      `${migrationBase()}/status`,
      { headers: authHeaders() }
    );
    return response.data?.state ?? "form";
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      clearAuthToken();
      return "form";
    }
    // Soft-fail on any other error — the user can still try to identify
    return "form";
  }
};

export const fetchEligibility = async (): Promise<EligibilityResult> => {
  try {
    const response = await axios.get<{
      eligible: boolean;
      reason: string | null;
      plan: PlanComparison | null;
    }>(`${migrationBase()}/eligibility`, { headers: authHeaders() });

    const { eligible, reason, plan } = response.data;

    if (eligible && plan) {
      return { kind: "eligible", plan };
    }
    if (reason === "comfort_mensal") {
      return { kind: "ineligible_comfort" };
    }
    if (reason === "already_migrated") {
      return { kind: "already_migrated" };
    }
    return { kind: "ineligible_other", reason };
  } catch (error) {
    return handle401(error);
  }
};

export interface ConfirmResult {
  success: true;
  scheduledFor?: string;
  alreadyMigrated?: boolean;
}

export const confirmMigration = async (): Promise<ConfirmResult> => {
  try {
    const response = await axios.post<ConfirmResult>(
      `${migrationBase()}/confirmar`,
      { confirmed: true },
      { headers: authHeaders() }
    );
    return response.data;
  } catch (error) {
    return handle401(error);
  }
};
